import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { neurons } from '../data/neurons';
import '../components/blueprint/blueprint.css';

const TYPE_LABEL = {
  employment: 'EXPERIENCE',
  project:    'PROJECT',
  skill:      'SKILL',
  education:  'EDUCATION',
  personal:   'PERSONAL',
};

const stagger = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};

const fadeUp = {
  hidden:  { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.1, 0.25, 1] } },
};

function Section({ number, title, children }) {
  return (
    <motion.div className="bpn-section" variants={fadeUp}>
      <div className="bpn-section-header">
        <span className="bpn-section-num">{number}</span>
        <div className="bpn-section-rule" />
        <span className="bpn-section-title">{title}</span>
      </div>
      {children}
    </motion.div>
  );
}

function NotFound() {
  return (
    <div className="bp-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div className="bp-grid-bg" />
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <p className="bp-label" style={{ justifyContent: 'center' }}>404 — NOT FOUND</p>
        <Link to="/" className="bpn-back-link" style={{ display: 'inline-block', marginTop: 24 }}>← BACK TO PORTFOLIO</Link>
      </div>
    </div>
  );
}

export default function BlueprintNodePage() {
  const { id } = useParams();
  const node = neurons.find(n => n.id === id);

  if (!node) return <NotFound />;

  const typeLabel = TYPE_LABEL[node.type] || node.type.toUpperCase();

  return (
    <div className="bp-root">
      <div className="bp-grid-bg" />

      {/* ── NAV ── */}
      <nav className="bp-nav">
        <Link to="/" className="bp-nav-logo">AS</Link>
        <Link to="/" className="bpn-back-link">← BACK TO PORTFOLIO</Link>
      </nav>

      {/* ── PAGE CONTENT ── */}
      <motion.main
        className="bpn-main"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >

        {/* ── HEADER ── */}
        <motion.div className="bpn-header" variants={fadeUp}>
          <p className="bp-label">{typeLabel} — {node.dates}</p>
          <h1 className="bpn-title">{node.label}</h1>
          <div className="bp-divider" style={{ maxWidth: 560 }} />
          {node.role && <p className="bpn-role">{node.role}</p>}
        </motion.div>

        {/* ── BODY ── */}
        <div className="bpn-body">

          {/* OVERVIEW */}
          {node.description && (
            <Section number="01" title="OVERVIEW">
              <p className="bpn-desc">{node.description}</p>
            </Section>
          )}

          {/* HIGHLIGHTS */}
          {node.achievements?.length > 0 && (
            <Section number="02" title="KEY HIGHLIGHTS">
              <ul className="bpn-achievements">
                {node.achievements.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </Section>
          )}

          {/* TOOLS & TECH */}
          {node.tech?.length > 0 && (
            <Section number="03" title="TOOLS & TECHNOLOGIES">
              <div className="bp-tags" style={{ marginTop: 16 }}>
                {node.tech.map(t => (
                  <span key={t} className="bp-tag bpn-tech-tag">{t}</span>
                ))}
              </div>
            </Section>
          )}

          {/* KEYWORDS */}
          {node.tags?.length > 0 && (
            <Section number="04" title="KEYWORDS">
              <div className="bp-tags" style={{ marginTop: 16 }}>
                {node.tags.map(tag => (
                  <span key={tag} className="bp-tag">{tag}</span>
                ))}
              </div>
            </Section>
          )}

        </div>

        {/* ── FOOTER ── */}
        <motion.div className="bpn-footer" variants={fadeUp}>
          <div className="bpn-footer-rule" />
          <Link to="/" className="bpn-back-link">← BACK TO PORTFOLIO</Link>
        </motion.div>

      </motion.main>
    </div>
  );
}
